import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { logException } from '@babylon/sentry'
import { UserExistsResponse } from '@babylon/consumer-api/types'
import { useFormatMessage } from '@babylon/intl'
import { ButtonSimple } from '@babylon/medkit'
import { Button, Box, Stack } from '@babylon/web-platform-ui'
import { checkExistingUser } from '@/utils'
import {
  useCurrentPageContent,
  useActiveStepIndex,
  useNextAction,
  useAbTestTracked,
} from '@/redux/signup/selectors'
import { useConfig, useContent } from '@/redux/partnerConfig/selectors'
import { FormRenderer, LanguageSwitcher, ProductFeatures } from '@/components'
import { Welcome } from '@/types/partnerConfig'
import { setUserProfile, setUserExists } from '@/redux/userProfile/actions'
import { useUserEmail } from '@/redux/userProfile/selectors'
import { trackTealiumEvent } from '@/tracking'
import {
  useTrackingScreenTitle,
  useAuth0Wrapper,
  useHandleExistingUser,
} from '@/hooks'
import {
  useAbTestName,
  useAbTestVariant,
} from '@/redux/productConfig/selectors'
import messages from './WelcomePage.messages'
import { setAbTestTracked } from '@/redux/signup/actions'
import welcomeImage from './assets/welcome_image.png'

const WelcomePage = () => {
  const translate = useFormatMessage()
  const dispatch = useDispatch()
  const nextAction = useNextAction()
  const handleExistingUser = useHandleExistingUser()
  const content = useCurrentPageContent()
  const partnerContent = useContent()
  const userEmail = useUserEmail()
  const abTestName = useAbTestName()
  const abTestVariant = useAbTestVariant()
  const { fields, useDnaV2Design, isAccountCreationV2 } = useConfig()
  const activeStepIndex = useActiveStepIndex()
  const { isAuthenticated } = useAuth0Wrapper()
  const stepTrackingName = useTrackingScreenTitle()
  const [loading, setLoading] = useState<boolean>(false)
  const abTestTracked = useAbTestTracked()
  const { productFeatures } = content as Welcome
  const submitButtonText = translate(messages.next)
  const createAccountButtonText = translate(messages.createAccount)
  const loginButtonText = translate(messages.login)
  const initialFormValues = {
    email: userEmail,
  }
  const availableLanguages = Object.keys(partnerContent)

  useEffect(() => {
    if (isAuthenticated && userEmail) {
      dispatch(setUserExists(true))
    }
  }, [dispatch, isAuthenticated, userEmail])

  useEffect(() => {
    if (abTestName && !abTestTracked) {
      trackTealiumEvent({
        screen_title: stepTrackingName,
        eventCategory: 'ab test',
        eventAction: 'enter test',
        tealium_event: 'enter test',
        ab_test_name: abTestName,
        ab_test_variant: String(abTestVariant),
      })
      dispatch(setAbTestTracked(true))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abTestName])

  const onNextAction = (trackSignup: boolean = true) => {
    if (trackSignup) {
      trackTealiumEvent({
        screen_title: stepTrackingName,
        eventCategory: 'signup',
        eventAction: 'started signup',
        eventLabel: 'email',
        tealium_event: 'started signup',
      })
    }

    nextAction()
  }

  const onCheckExistingUserResponse = (
    data: UserExistsResponse,
    email: string
  ) => {
    const userExists = data?.exists
    dispatch(setUserExists(userExists))

    if (userExists && !isAuthenticated) {
      handleExistingUser(email)
      setLoading(false)
    } else {
      onNextAction()
    }
  }

  const onSubmit = async (values: typeof initialFormValues) => {
    const { email } = values

    if (email) {
      /**
       * Check if User already exists
       */
      try {
        setLoading(true)
        const response = await checkExistingUser(email)
        onCheckExistingUserResponse(response.data, email)
      } catch (error) {
        logException(error)
        onNextAction()
      }
    }

    dispatch(setUserProfile({ ...values }))
  }

  const onCreateAccount = () => {
    onNextAction()
  }

  if (useDnaV2Design && isAccountCreationV2) {
    return (
      <Box marginBottom={9}>
        <img src={welcomeImage} alt="Welcome" />
        <Stack spacing={4} marginTop={25}>
          <Button
            type="button"
            isFullWidth
            isLoading={false}
            data-testid="form-submit-button"
            variant="primary"
            onClick={onCreateAccount}
          >
            {createAccountButtonText}
          </Button>
          <Button
            type="button"
            isFullWidth
            isLoading={false}
            data-testid="form-submit-button"
            variant="secondary"
          >
            {loginButtonText}
          </Button>
        </Stack>
      </Box>
    )
  }

  return (
    <>
      <ProductFeatures features={productFeatures} />
      {isAuthenticated && userEmail ? (
        <>
          {useDnaV2Design && (
            <Button
              onClick={() => onNextAction(false)}
              variant="primary"
              isFullWidth
            >
              {submitButtonText}
            </Button>
          )}
          {!useDnaV2Design && (
            <ButtonSimple onClick={() => onNextAction(false)} fullWidth>
              {submitButtonText}
            </ButtonSimple>
          )}
        </>
      ) : (
        <FormRenderer
          testId="welcome-page-form"
          fields={fields[activeStepIndex]}
          onSubmit={onSubmit}
          initialValues={initialFormValues}
          submitButtonText={submitButtonText}
          loading={loading}
        />
      )}
      <LanguageSwitcher
        useDnaV2Design={Boolean(useDnaV2Design)}
        languages={availableLanguages}
      />
    </>
  )
}

export default WelcomePage
